import { useState, useEffect } from "react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { X, Film, Cloud } from "lucide-react";

const fmtSize = (b) => (b >= 1024 ** 3 ? (b / 1024 ** 3).toFixed(2) + " GB" : (b / 1048576).toFixed(1) + " MB");
const isVideo = (k) => /\.(mp4|webm|mov|mkv|m4v)$/i.test(k);

export const VideoPicker = ({ onPick, onClose }) => {
  const [data, setData] = useState(null);
  const [importing, setImporting] = useState("");

  useEffect(() => {
    api.get("/admin/storage").then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickExternal = async (o) => {
    setImporting(o.key);
    try {
      const r = await api.post("/admin/storage/import", { key: o.key });
      onPick(r.data.url);
    } catch (e) { toast.error(errMsg(e)); }
    setImporting("");
  };

  const externals = (data?.external || []).filter((o) => isVideo(o.key));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.72)" }} onClick={onClose} data-testid="video-picker-modal">
      <div className="forge-card w-full max-w-2xl max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-sm" style={{ letterSpacing: 1 }}>CHOOSE A VIDEO FROM STORAGE</h3>
          <button onClick={onClose} data-testid="video-picker-close" className="hover:text-red-400" style={{ color: "var(--muted)" }}><X size={16} /></button>
        </div>
        {!data ? <p className="text-sm" style={{ color: "var(--muted)" }}>Loading storage…</p> : (
          <>
            {data.files.length > 0 && (
              <div className="mb-4">
                <p className="text-xs mb-2 flex items-center gap-1" style={{ color: "var(--muted)" }}><Film size={12} /> UPLOADED VIA THE APP</p>
                {data.files.map((f) => (
                  <button key={f.id} type="button" onClick={() => onPick(f.url)} data-testid={`picker-file-${f.id}`}
                    className="w-full text-left flex justify-between items-center px-3 py-2 rounded hover:bg-white/5 text-sm">
                    <span className="truncate">{f.filename}</span>
                    <span className="text-xs flex-none ml-3" style={{ color: "var(--gold)" }}>{fmtSize(f.total_size)}</span>
                  </button>
                ))}
              </div>
            )}
            {externals.length > 0 && (
              <div>
                <p className="text-xs mb-1 flex items-center gap-1" style={{ color: "var(--muted)" }}><Cloud size={12} /> UPLOADED DIRECTLY TO CLOUDFLARE</p>
                <p className="text-xs mb-2" style={{ color: "var(--bp)" }}>Picking one links it to the app with protected playback — no re-upload needed.</p>
                {externals.map((o) => (
                  <button key={o.key} type="button" disabled={!!importing} onClick={() => pickExternal(o)} data-testid={`picker-ext-${o.key}`}
                    className="w-full text-left flex justify-between items-center px-3 py-2 rounded hover:bg-white/5 text-sm disabled:opacity-50">
                    <span className="truncate">{importing === o.key ? "Linking… " : ""}{o.key}</span>
                    <span className="text-xs flex-none ml-3" style={{ color: "var(--gold)" }}>{fmtSize(o.size)}</span>
                  </button>
                ))}
              </div>
            )}
            {data.files.length === 0 && externals.length === 0 && (
              <p className="text-sm" style={{ color: "var(--muted)" }}>No videos in storage yet — upload some on the Storage page first.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
